import { useRef } from 'react'
import { Sparkles, FileDown, X, AlertTriangle, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { PDFDownloadTrigger, PDFDownloadTriggerHandle } from '@/components/common/pdf-download-trigger'

import { useToast } from '@/hooks/use-toast'
import { sanitizeHtml } from '@/lib/sanitize'

import { SignsSymptomProps, formatted_signs_symptom_category, countCheckedSymptoms, signs_symptom_status, formatted_signs_symptom_children_range } from '@/entities/signs-symptom'

interface ResultDialogProps {
  open: boolean
  record: SignsSymptomProps | null
  onClose: () => void
  /** @deprecated — Download agora é interno via PDFDownloadTrigger. */
  onDownloadPdf?: () => void
}

function buildSubtitle(record: SignsSymptomProps): string {
  const parts: string[] = [formatted_signs_symptom_category[record.category]]
  if (record.weeksOfGestation) parts.push(`${record.weeksOfGestation} semanas de gestação`)
  if (record.childrenRange) parts.push(formatted_signs_symptom_children_range[record.childrenRange])
  return parts.join(' · ')
}

function buildFilename(record: SignsSymptomProps): string {
  const slug = (record.patientName || 'paciente').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const today = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')
  return `sinais-sintomas-${slug}-${today}.pdf`
}

export function ResultDialog({ open, record, onClose }: ResultDialogProps) {
  const { toast } = useToast()
  const triggerRef = useRef<PDFDownloadTriggerHandle>(null)

  if (!record) return null

  const hasAnalysis = record.status === signs_symptom_status.ACTIVE && !!record.contentHtml
  const isProcessing = record.status === signs_symptom_status.PROCESSING
  const isFailed = record.status === signs_symptom_status.FAILED
  const totalSymptoms = countCheckedSymptoms(record)

  const handleDownloadPdf = async () => {
    if (!hasAnalysis) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'A análise ainda não está disponível.' })
      return
    }
    try {
      toast({ variant: 'default', title: 'Gerando PDF…', description: 'Aguarde enquanto preparamos o arquivo.' })
      await triggerRef.current?.download({ filename: buildFilename(record) })
    } catch (err) {
      console.error('Erro ao gerar PDF:', err)
      toast({ variant: 'destructive', title: 'Ops…', description: 'Não foi possível gerar o PDF.' })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={open => !open && onClose()}>
        <DialogContent className="max-w-3xl w-[min(900px,calc(100vw-32px))] p-0 overflow-hidden">
          <div
            className="px-6 py-5 flex items-start justify-between gap-4 text-white"
            style={{
              backgroundImage: 'linear-gradient(rgba(0,0,0,0.08), rgba(0,0,0,0.2)), linear-gradient(135deg, hsl(var(--coentro)) 0%, hsl(var(--primary)) 100%)'
            }}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-white/80 mb-1">
                <Sparkles className="h-3 w-3" />
                <span>Análise da Nina · Mãe Nutrida</span>
              </div>
              <DialogTitle className="font-display text-xl font-semibold text-white drop-shadow-sm">{record.patientName}</DialogTitle>
              <DialogDescription className="text-xs text-white/80 mt-1">
                {formatted_signs_symptom_category[record.category]} · {totalSymptoms} sintoma{totalSymptoms !== 1 ? 's' : ''} avaliado{totalSymptoms !== 1 ? 's' : ''}
              </DialogDescription>
            </div>
            <button type="button" onClick={onClose} className="flex items-center justify-center h-8 w-8 rounded-md text-white/85 hover:bg-white/15 transition-colors shrink-0" aria-label="Fechar">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
            <section>
              <h3 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground mb-2">Análise da Nina</h3>
              {hasAnalysis ? (
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: sanitizeHtml(record.contentHtml) }} />
              ) : isProcessing ? (
                <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 px-5 py-8 text-center">
                  <div className="flex items-center justify-center h-14 w-14 rounded-full bg-amber-100 text-amber-700 mx-auto mb-3">
                    <Loader2 className="h-7 w-7 animate-spin" />
                  </div>
                  <h4 className="text-base font-semibold text-amber-900">Análise em processamento</h4>
                  <p className="text-sm text-amber-800 max-w-md mx-auto mt-2">A Nina está analisando os sintomas. Você será notificado quando a análise estiver pronta.</p>
                </div>
              ) : isFailed ? (
                <div className="rounded-2xl border border-dashed border-red-300 bg-red-50 px-5 py-8 text-center">
                  <div className="flex items-center justify-center h-14 w-14 rounded-full bg-red-100 text-red-700 mx-auto mb-3">
                    <AlertTriangle className="h-7 w-7" />
                  </div>
                  <h4 className="text-base font-semibold text-red-900">Falha na geração</h4>
                  <p className="text-sm text-red-800 max-w-md mx-auto mt-2">Não foi possível concluir a análise. Você pode tentar gerar novamente.</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 px-5 py-8 text-center">
                  <div className="flex items-center justify-center h-14 w-14 rounded-full bg-primary/15 text-coentro mx-auto mb-3">
                    <AlertTriangle className="h-7 w-7" />
                  </div>
                  <h4 className="text-base font-semibold text-foreground">Análise ainda não gerada</h4>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto mt-2">
                    Edite o registro e clique em <strong>Gerar análise</strong> para que a Nina produza hipóteses clínicas, nutrientes prioritários e exames sugeridos com base nos sintomas marcados.
                  </p>
                </div>
              )}
            </section>
          </div>

          <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-3 smAndDown:flex-col">
            <p className="text-[11px] text-muted-foreground">💡 Use as hipóteses como ponto de partida — valide com exames.</p>
            <div className="flex gap-2 smAndDown:w-full">
              <Button variant="outline" size="sm" onClick={onClose} className="smAndDown:flex-1">
                Fechar
              </Button>
              <Button size="sm" onClick={handleDownloadPdf} disabled={!hasAnalysis} className="gap-2 smAndDown:flex-1">
                <FileDown className="h-4 w-4" />
                Baixar PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template off-screen para geração do PDF */}
      <PDFDownloadTrigger
        ref={triggerRef}
        active={hasAnalysis}
        title="Análise de Sinais e Sintomas"
        patientName={record.patientName}
        subtitle={buildSubtitle(record)}
        contentHtml={record.contentHtml || ''}
      />
    </>
  )
}
